import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Filter, ChevronDown, ChevronRight, MapPin, FileText, Check, X, Clock, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { LoadingState } from '@/components/ui/LoadingState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card/Card';
import { VStack, HStack } from '@/components/ui/Stack';
import { Select } from '@/components/ui/Select/Select';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { VenueSelectionCard, type VenueSelectionCardShortlistItem } from '@/components/ui/VenueSelectionCard';
import { HotelNotesPanel } from '../../NoteIcon';
import { FileViewerModal, type FileViewerFile } from '@/components/ui/FileViewer/FileViewerModal';
import { VenueDetailsModal } from '../Step1Research/VenueDetailsModal';
import { MoveToDestinationModal, type DestinationOption } from '@/components/venues/MoveToDestinationModal';
import { ProposalBreakdownModal } from './ProposalBreakdownModal';
import { useNotificationStore } from '@/stores/notificationStore';
import { 
  ResearchService, 
  type SubmittedProposalsResponse, 
  type ShortlistWithVenue,
  type ResearchDestination,
  type VenueDetails
} from '@/services/ResearchService';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/utils';

interface SelectionStepProps {
  eventId: string;
  onBack: () => void;
  onContinueToProposal?: () => void;
}

type ApprovalFilter = 'all' | 'pending' | 'approved' | 'denied';

interface CityGroup {
  city: string;
  country: string;
  destinations: Array<{
    destination: ResearchDestination;
    shortlist: ShortlistWithVenue[];
  }>;
}

export function SelectionStep({ eventId, onBack, onContinueToProposal }: SelectionStepProps) {
  const [data, setData] = useState<SubmittedProposalsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ApprovalFilter>('all');
  const [expandedCities, setExpandedCities] = useState<Set<string>>(new Set());
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<FileViewerFile | null>(null);
  const [selectedVenueDetails, setSelectedVenueDetails] = useState<VenueDetails | null>(null);
  const [isLoadingVenueDetails, setIsLoadingVenueDetails] = useState(false);
  
  // Proposal breakdown modal state
  const [breakdownModalOpen, setBreakdownModalOpen] = useState(false);
  const [selectedProposalData, setSelectedProposalData] = useState<any>(null);
  const [selectedVenueName, setSelectedVenueName] = useState<string>('');
  const [selectedCurrencyCode, setSelectedCurrencyCode] = useState<string>('USD');
  
  // Move to destination modal state
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [movingShortlistId, setMovingShortlistId] = useState<string | null>(null);
  const [movingVenueName, setMovingVenueName] = useState<string>('');
  const [isMoving, setIsMoving] = useState(false);
  
  const researchService = new ResearchService();
  const notificationStore = useNotificationStore();
  const queryClient = useQueryClient();
  
  useEffect(() => {
    loadData();
  }, [eventId, filter]);
  
  async function loadData() {
    try {
      setLoading(true);
      const result = await researchService.getSubmittedProposals(eventId, filter);
      setData(result);
      
      // Expand all cities by default
      const allCityKeys = result.grouped_by_city.map(g => `${g.city}-${g.country}`);
      setExpandedCities(new Set(allCityKeys));
    } catch (error) {
      console.error('Failed to load submitted proposals:', error);
      notificationStore.error('Failed to load submitted proposals', { title: 'Load Error' });
    } finally {
      setLoading(false);
    }
  }
  
  // decision_note is no longer written here (the shared hotel note replaces
  // it); omitting it keeps historical decision notes intact.
  async function handleApprove(shortlistId: string) {
    try {
      setUpdatingId(shortlistId);
      await researchService.updateApprovalDecision(shortlistId, {
        approval_status: 'approved',
      });
      notificationStore.success('Venue approved successfully');
      await loadData();
      queryClient.invalidateQueries({ queryKey: ['research-proposal-shortlist', eventId] });
    } catch (error) {
      console.error('Failed to approve venue:', error);
      notificationStore.error('Failed to approve venue', { title: 'Update Error' });
    } finally {
      setUpdatingId(null);
    }
  }
  
  // Saves the hotel note shared with the Research and RFP steps.
  async function handleUpdateNotes(shortlistId: string, notes: string, clientFacing: boolean) {
    try {
      await researchService.updateShortlistItem(shortlistId, { notes, notes_client_facing: clientFacing });
      updateLocalShortlistItem(shortlistId, { notes, notes_client_facing: clientFacing });
      notificationStore.success('Notes saved', { title: 'Success' });
      queryClient.invalidateQueries({ queryKey: ['research-proposal-shortlist', eventId] });
    } catch (error) {
      console.error('Failed to update notes:', error);
      notificationStore.error('Failed to save notes', { title: 'Error' });
      throw error; // Keep the notes editor open with the unsaved draft
    }
  }

  async function handleDeny(shortlistId: string) {
    try {
      setUpdatingId(shortlistId);
      await researchService.updateApprovalDecision(shortlistId, {
        approval_status: 'denied',
      });
      notificationStore.success('Venue denied');
      await loadData();
      queryClient.invalidateQueries({ queryKey: ['research-proposal-shortlist', eventId] });
    } catch (error) {
      console.error('Failed to deny venue:', error);
      notificationStore.error('Failed to deny venue', { title: 'Update Error' });
    } finally {
      setUpdatingId(null);
    }
  }

  function updateLocalShortlistItem(shortlistId: string, updates: Partial<ShortlistWithVenue>) {
    if (!data) return;

    setData({
      ...data,
      grouped_by_city: data.grouped_by_city.map(cityGroup => ({
        ...cityGroup,
        destinations: cityGroup.destinations.map(destGroup => ({
          ...destGroup,
          shortlist: destGroup.shortlist.map(item =>
            item.id === shortlistId ? { ...item, ...updates } : item
          ),
        })),
      })),
      destinations: data.destinations.map(destGroup => ({
        ...destGroup,
        shortlist: destGroup.shortlist.map(item =>
          item.id === shortlistId ? { ...item, ...updates } : item
        ),
      })),
    });
  }
  
  async function handleToggleRecommended(shortlistId: string, isRecommended: boolean) {
    try {
      setUpdatingId(shortlistId);
      updateLocalShortlistItem(shortlistId, { is_recommended: isRecommended });
      
      // Update on server
      await researchService.updateShortlistItem(shortlistId, {
        is_recommended: isRecommended,
      });
      
      notificationStore.success(isRecommended ? 'Venue marked as recommended' : 'Recommendation removed');
      
      // Invalidate queries in background (don't wait for it)
      queryClient.invalidateQueries({ queryKey: ['research-proposal-shortlist', eventId] });
    } catch (error) {
      console.error('Failed to update recommendation:', error);
      notificationStore.error('Failed to update recommendation', { title: 'Update Error' });
      // Reload data on error to sync with server
      await loadData();
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleToggleBuyout(shortlistId: string, isBuyout: boolean) {
    try {
      setUpdatingId(shortlistId);
      updateLocalShortlistItem(shortlistId, { is_buyout: isBuyout });

      await researchService.updateShortlistItem(shortlistId, {
        is_buyout: isBuyout,
      });

      notificationStore.success(isBuyout ? 'Venue marked as buyout' : 'Buyout marker removed');
      queryClient.invalidateQueries({ queryKey: ['research-proposal-shortlist', eventId] });
    } catch (error) {
      console.error('Failed to update buyout marker:', error);
      notificationStore.error('Failed to update buyout marker', { title: 'Update Error' });
      await loadData();
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleToggleAllInclusive(shortlistId: string, isAllInclusive: boolean) {
    try {
      setUpdatingId(shortlistId);
      updateLocalShortlistItem(shortlistId, { is_all_inclusive: isAllInclusive });

      await researchService.updateShortlistItem(shortlistId, {
        is_all_inclusive: isAllInclusive,
      });

      notificationStore.success(isAllInclusive ? 'Venue marked as all-inclusive' : 'All-inclusive marker removed');
      queryClient.invalidateQueries({ queryKey: ['research-proposal-shortlist', eventId] });
    } catch (error) {
      console.error('Failed to update all-inclusive marker:', error);
      notificationStore.error('Failed to update all-inclusive marker', { title: 'Update Error' });
      await loadData();
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleComparisonBudgetNoteChange(shortlistId: string, note: string | null) {
    try {
      setUpdatingId(shortlistId);
      updateLocalShortlistItem(shortlistId, { comparison_budget_note: note });

      await researchService.updateShortlistItem(shortlistId, {
        comparison_budget_note: note,
      });

      notificationStore.success('Comparison budget details saved');
      queryClient.invalidateQueries({ queryKey: ['research-proposal-shortlist', eventId] });
    } catch (error) {
      console.error('Failed to save comparison budget details:', error);
      notificationStore.error('Failed to save comparison budget details', { title: 'Update Error' });
      await loadData();
    } finally {
      setUpdatingId(null);
    }
  }
  
  async function handleViewProposal(shortlistId: string) {
    try {
      // Find the shortlist item to get the PDF attachment
      const item = findShortlistItem(shortlistId);
      if (!item?.cvent_proposal_pdf_attachment_id) {
        notificationStore.error('No proposal PDF available');
        return;
      }
      
      // Get the attachment to retrieve the R2 URL
      const response = await apiFetch(`/api/attachments/${item.cvent_proposal_pdf_attachment_id}`);
      if (!response.ok) {
        notificationStore.error('Failed to load proposal PDF');
        return;
      }
      
      const result = await response.json();
      const attachment = result.data;
      
      setSelectedFile({
        url: attachment.url,
        name: `${item.venue.property_name} Proposal.pdf`,
        mimeType: 'application/pdf',
      });
    } catch (error) {
      console.error('Failed to view proposal:', error);
      notificationStore.error('Failed to load proposal PDF', { title: 'Load Error' });
    }
  }
  
  function findShortlistItem(shortlistId: string): ShortlistWithVenue | undefined {
    if (!data) return undefined;
    
    for (const dest of data.destinations) {
      const item = dest.shortlist.find(s => s.id === shortlistId);
      if (item) return item;
    }
    return undefined;
  }
  
  async function handleViewDetails(venueId: string) {
    try {
      setIsLoadingVenueDetails(true);
      const venueDetails = await researchService.getVenueDetails(venueId);
      setSelectedVenueDetails(venueDetails);
    } catch (error) {
      console.error('Failed to load venue details:', error);
      notificationStore.error('Failed to load venue details', { title: 'Load Error' });
    } finally {
      setIsLoadingVenueDetails(false);
    }
  }

  async function handleViewBreakdown(shortlistId: string) {
    try {
      const item = findShortlistItem(shortlistId);
      if (!item) {
        notificationStore.error('Venue not found');
        return;
      }

      // Get the full shortlist item to access JSONB data and summary
      const shortlistItem = await researchService.getShortlistItem(shortlistId);
      
      if (!shortlistItem.cvent_proposal_data_jsonb) {
        notificationStore.error('Proposal breakdown data not available. Please refresh proposals first.');
        return;
      }

      setSelectedProposalData({
        ...shortlistItem.cvent_proposal_data_jsonb,
        _summary: shortlistItem.cvent_proposal_summary, // Pass summary through proposalData
      });
      setSelectedVenueName(item.venue.property_name);
      setSelectedCurrencyCode(shortlistItem.cvent_currency_code || 'USD');
      setBreakdownModalOpen(true);
    } catch (error) {
      console.error('Failed to load proposal breakdown:', error);
      notificationStore.error('Failed to load proposal breakdown', { title: 'Load Error' });
    }
  }
  
  function handleMoveClick(shortlistId: string, venueName: string) {
    setMovingShortlistId(shortlistId);
    setMovingVenueName(venueName);
    setMoveModalOpen(true);
  }
  
  async function handleMoveVenue(destinationId: string) {
    if (!movingShortlistId) return;
    
    setIsMoving(true);
    try {
      await researchService.moveShortlistItem(movingShortlistId, destinationId);
      
      // Reload data to reflect the change
      await loadData();
      
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['research-proposal-shortlist', eventId] });
      
      notificationStore.success('Venue moved successfully', {
        title: 'Venue Moved',
      });
      
      setMoveModalOpen(false);
      setMovingShortlistId(null);
      setMovingVenueName('');
    } catch (error: any) {
      console.error('Failed to move venue:', error);
      notificationStore.error(error.message || 'Failed to move venue', {
        title: 'Move Error',
      });
    } finally {
      setIsMoving(false);
    }
  }
  
  // Get all destinations for the event (for move modal)
  const allDestinations: DestinationOption[] = useMemo(() => {
    if (!data) return [];
    return data.destinations.map(d => ({
      id: d.destination.id,
      city: d.destination.city,
      country: d.destination.country,
    }));
  }, [data]);
  
  function toggleCityExpanded(cityKey: string) {
    setExpandedCities(prev => {
      const next = new Set(prev);
      if (next.has(cityKey)) {
        next.delete(cityKey);
      } else {
        next.add(cityKey);
      }
      return next;
    });
  }
  
  // Transform shortlist item to VenueSelectionCard format
  function toCardItem(item: ShortlistWithVenue): VenueSelectionCardShortlistItem {
    return {
      id: item.id,
      venue: {
        id: item.venue.id,
        property_name: item.venue.property_name,
        city: item.venue.city,
        country: item.venue.country,
        number_of_rooms: item.venue.number_of_rooms,
        maximum_capacity: item.venue.maximum_capacity,
        star_rating: item.venue.star_rating,
        place_id: item.venue.place_id,
        website: item.venue.website,
        address: item.venue.address,
      },
      cvent_proposal_status: item.cvent_proposal_status,
      cvent_lowest_room_rate: item.cvent_lowest_room_rate,
      cvent_total_cost: item.cvent_total_cost,
      cvent_currency_code: item.cvent_currency_code,
      cvent_proposal_pdf_attachment_id: item.cvent_proposal_pdf_attachment_id,
      approval_status: item.approval_status,
      is_recommended: item.is_recommended || false,
      is_buyout: item.is_buyout || false,
      is_all_inclusive: item.is_all_inclusive || false,
      comparison_budget_note: item.comparison_budget_note || null,
    };
  }
  
  if (loading) {
    return <LoadingState text="Loading submitted proposals..." />;
  }
  
  // Empty state - no submitted proposals
  if (!data || data.totals.submitted === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="max-w-md w-full" padding="lg">
          <CardContent>
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  No Submitted Proposals
                </h2>
                <p className="text-muted-foreground">
                  None of your shortlisted venues have submitted proposals yet. 
                  Wait for venues to respond to your RFPs, then return here to review and approve them.
                </p>
              </div>
              
              <div className="pt-2">
                <button
                  onClick={onBack}
                  className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                >
                  ← Back to RFPs
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasApprovedVenues = data.totals.approved > 0;
  
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b bg-white px-4 py-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          {/* Left side: Back button + Title */}
          <div className="flex items-start gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0 mt-0.5"
              aria-label="Back to RFPs"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Review & Finalize Venues
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                Review submitted proposals and decide which venues to include in your proposal
              </p>
            </div>
          </div>
          
          {/* Right side: Continue to Proposal */}
          <div className="flex items-center gap-2 flex-shrink-0 sm:mt-0.5">
            <SimpleTooltip
              content={
                !hasApprovedVenues 
                  ? "Approve at least one venue to continue" 
                  : `Continue with ${data.totals.approved} approved venue${data.totals.approved > 1 ? 's' : ''}`
              }
            >
              <span>
                <Button
                  variant={hasApprovedVenues ? "primary" : "outline"}
                  size="sm"
                  rightIcon={<ArrowRight className="w-4 h-4" />}
                  onClick={onContinueToProposal}
                  disabled={!hasApprovedVenues || !onContinueToProposal}
                  className={cn(
                    "relative transition-all",
                    hasApprovedVenues && "shadow-sm hover:shadow-md"
                  )}
                >
                  <span className="hidden sm:inline">Continue to Proposal</span>
                  <span className="sm:hidden">Proposal</span>
                  {hasApprovedVenues && (
                    <Badge 
                      variant="primary-inverted" 
                      className="ml-2 px-1.5 py-0 text-xs h-5 min-w-[20px] flex items-center justify-center"
                    >
                      {data.totals.approved}
                    </Badge>
                  )}
                </Button>
              </span>
            </SimpleTooltip>
          </div>
        </div>
        
        {/* Filter & Stats Row */}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Filter */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              leftIcon={<Filter className="w-4 h-4" />}
              onClick={() => {}} // Filter is handled by the select
              className="pointer-events-none"
            >
              <span className="sr-only">Filter</span>
            </Button>
            <Select
              value={filter}
              onValueChange={(value) => setFilter(value as ApprovalFilter)}
              options={[
                { value: 'all', label: 'All Proposals' },
                { value: 'pending', label: 'Pending Decision' },
                { value: 'approved', label: 'Approved' },
                { value: 'denied', label: 'Denied' },
              ]}
              className="w-44 h-8 text-sm"
            />
          </div>
          
          {/* Stats Badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className="flex items-center gap-1 h-7 px-2.5">
              <Building2 className="w-3 h-3" />
              <span className="text-xs">{data.totals.submitted} proposals</span>
            </Badge>
            <Badge variant="warning" className="flex items-center gap-1 h-7 px-2.5">
              <Clock className="w-3 h-3" />
              <span className="text-xs">{data.totals.pending} pending</span>
            </Badge>
            <Badge variant="success" className="flex items-center gap-1 h-7 px-2.5">
              <Check className="w-3 h-3" />
              <span className="text-xs">{data.totals.approved} approved</span>
            </Badge>
            <Badge variant="error" className="flex items-center gap-1 h-7 px-2.5">
              <X className="w-3 h-3" />
              <span className="text-xs">{data.totals.denied} denied</span>
            </Badge>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 overflow-y-auto" style={{ backgroundColor: '#efede7' }}>
        <div className="p-4 sm:p-6">
          <VStack gap={6}>
            {data.grouped_by_city.map((cityGroup) => {
              const cityKey = `${cityGroup.city}-${cityGroup.country}`;
              const isExpanded = expandedCities.has(cityKey);
              const venueCount = cityGroup.destinations.reduce(
                (sum, d) => sum + d.shortlist.length,
                0
              );
              // Check if this group is a region (from group-level fields or first destination)
              const isRegion = (cityGroup as any).destination_type === 'region'
                || cityGroup.destinations[0]?.destination?.destination_type === 'region';
              const groupLabel = (cityGroup as any).display_name
                || cityGroup.destinations[0]?.destination?.display_name
                || cityGroup.city;

              return (
                <Card key={cityKey} className="overflow-hidden">
                  {/* City Header */}
                  <button
                    onClick={() => toggleCityExpanded(cityKey)}
                    className="w-full px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors border-b"
                  >
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-primary-600" />
                      <div className="text-left">
                        <h2 className="font-semibold text-gray-900">
                          {groupLabel}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                          {cityGroup.country} · {venueCount} venue{venueCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </button>
                  
                  {/* Venues Grid */}
                  {isExpanded && (
                    <CardContent className="p-4 sm:p-6">
                      <VStack gap={4}>
                        {cityGroup.destinations.flatMap((dest) =>
                          dest.shortlist.map((item) => (
                            <VenueSelectionCard
                              key={item.id}
                              item={toCardItem(item)}
                              onApprove={handleApprove}
                              onDeny={handleDeny}
                              notesSection={
                                <HotelNotesPanel
                                  notes={item.notes}
                                  clientFacing={item.notes_client_facing}
                                  venueName={item.venue.property_name}
                                  onUpdateNotes={(notes, clientFacing) => handleUpdateNotes(item.id, notes, clientFacing)}
                                />
                              }
                              onToggleRecommended={handleToggleRecommended}
                              onToggleBuyout={handleToggleBuyout}
                              onToggleAllInclusive={handleToggleAllInclusive}
                              onComparisonBudgetNoteChange={handleComparisonBudgetNoteChange}
                              onViewProposal={handleViewProposal}
                              onViewDetails={handleViewDetails}
                              onViewBreakdown={(shortlistId) => handleViewBreakdown(shortlistId)}
                              onMoveToDestination={allDestinations.length > 1 
                                ? (shortlistId) => handleMoveClick(shortlistId, item.venue.property_name)
                                : undefined
                              }
                              isUpdating={updatingId === item.id}
                            />
                          ))
                        )}
                      </VStack>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </VStack>
        </div>
      </div>
      
      {/* File Viewer Modal for Proposal PDFs */}
      <FileViewerModal
        isOpen={!!selectedFile}
        onClose={() => setSelectedFile(null)}
        file={selectedFile}
      />
      
      {/* Venue Details Modal */}
      <VenueDetailsModal
        isOpen={!!selectedVenueDetails}
        onClose={() => setSelectedVenueDetails(null)}
        venue={selectedVenueDetails}
        isShortlisted={true}
      />
      
      {/* Move to Destination Modal */}
      <MoveToDestinationModal
        isOpen={moveModalOpen}
        onClose={() => {
          setMoveModalOpen(false);
          setMovingShortlistId(null);
          setMovingVenueName('');
        }}
        onMove={handleMoveVenue}
        destinations={allDestinations}
        venueName={movingVenueName}
        allowCreateNew={false}
        isMoving={isMoving}
      />

      {/* Proposal Breakdown Modal */}
      <ProposalBreakdownModal
        isOpen={breakdownModalOpen}
        onClose={() => {
          setBreakdownModalOpen(false);
          setSelectedProposalData(null);
          setSelectedVenueName('');
          setSelectedCurrencyCode('USD');
        }}
        proposalData={selectedProposalData}
        venueName={selectedVenueName}
        currencyCode={selectedCurrencyCode}
      />
    </div>
  );
}
